import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, ListChecks, Building2, Users, FileText, LogOut, Menu, Search, Plus, Bell, X, CheckCheck,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { api, timeAgo } from "../../lib/api";
import { Avatar, HarrodsMark } from "./bits";
import { EP_LOGO } from "../../pages/Login";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import TaskSheet from "./TaskSheet";
import Dialogs from "./Dialogs";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
  { to: "/customers", label: "Customers", icon: Building2 },
  { to: "/team", label: "Team Members", icon: Users },
  { to: "/documents", label: "Documents", icon: FileText },
];

const TITLES = { "/": "Dashboard", "/projects": "Projects", "/tasks": "Tasks", "/customers": "Customers", "/team": "Team Members", "/documents": "Documents" };

function Sidebar({ onNavigate }) {
  const { user, logout } = useApp();
  return (
    <aside className="flex h-full w-64 flex-col border-r border-black/20 bg-[#0c2a20]">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <img alt="EP Group" className="h-8 w-auto" src={EP_LOGO} />
      </div>
      <div className="mx-5 mb-3 h-px bg-gradient-to-r from-[#b8975a]/60 to-transparent" />
      <nav className="flex-1 space-y-1 px-3 py-2">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-[#b8975a] text-[#0c2a20]" : "text-[#c9d6ce] hover:bg-white/5 hover:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <n.icon className={`h-[18px] w-[18px] ${isActive ? "text-[#0c2a20]" : "text-[#8fa89a] group-hover:text-white"}`} />
                {n.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-3 rounded-lg bg-white/5 p-3">
          <Avatar person={user} size={36} className="ring-0" />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
            <p className="truncate text-[11px] text-[#9fb3a7]">{user?.role}</p>
          </div>
          <button title="Switch user" data-testid="logout-btn" onClick={logout} className="rounded-md p-1.5 text-[#9fb3a7] hover:bg-white/10 hover:text-white">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { openTask } = useApp();
  const ref = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setRes(null);
      return;
    }
    const t = setTimeout(async () => {
      const r = await api.get("/search", { params: { q } });
      setRes(r.data);
      setOpen(true);
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (fn) => {
    fn();
    setOpen(false);
    setQ("");
  };
  const empty = res && !res.projects.length && !res.tasks.length && !res.team.length && !res.documents.length;

  return (
    <div ref={ref} className="relative ml-auto hidden max-w-xs flex-1 sm:block">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        data-testid="global-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => res && setOpen(true)}
        placeholder="Search this app..."
        className="h-9 border-slate-200 bg-slate-50 pl-9 text-sm focus-visible:bg-white"
      />
      {open && res && (
        <div className="absolute right-0 top-11 z-50 max-h-96 w-full min-w-[320px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl" data-testid="search-results">
          {empty && <p className="p-3 text-sm text-slate-400">No results for “{q}”</p>}
          {[
            ["Projects", res.projects, (x) => x.name, (x) => () => nav(`/projects/${x.id}`)],
            ["Tasks", res.tasks, (x) => x.title, (x) => () => openTask(x.id)],
            ["Team", res.team, (x) => `${x.name} · ${x.role}`, () => () => nav("/team")],
            ["Documents", res.documents, (x) => x.name, () => () => nav("/documents")],
          ].map(([label, items, text, action]) =>
            items.length ? (
              <div key={label} className="mb-1">
                <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
                {items.map((x) => (
                  <button key={x.id} onClick={() => go(action(x))} className="block w-full truncate rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-[#f5efe3]">
                    {text(x)}
                  </button>
                ))}
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const { user, notifications, loadNotifications, setNotifications, openTask } = useApp();
  const nav = useNavigate();
  const click = async (n) => {
    if (!n.read) await api.post(`/notifications/item/${n.id}/read`);
    loadNotifications();
    if (n.scope === "task") openTask(n.refId);
    else nav(`/projects/${n.refId}`);
  };
  const readAll = async () => {
    await api.post(`/notifications/${user.id}/read-all`);
    setNotifications((s) => ({ items: s.items.map((i) => ({ ...i, read: true })), unread: 0 }));
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100" data-testid="notifications-btn">
          <Bell className="h-5 w-5" />
          {notifications.unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#b8975a] px-1 text-[10px] font-bold text-white" data-testid="notifications-count">
              {notifications.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="text-sm font-bold text-slate-900">Notifications</p>
          {notifications.unread > 0 && (
            <button onClick={readAll} className="flex items-center gap-1 text-xs font-semibold text-[#00573f] hover:underline" data-testid="mark-all-read">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.items.length === 0 && <p className="p-6 text-center text-sm text-slate-400">No notifications yet.</p>}
          {notifications.items.map((n) => (
            <DropdownMenuItem key={n.id} onClick={() => click(n)} className={`flex cursor-pointer items-start gap-3 rounded-none px-4 py-3 ${n.read ? "" : "bg-[#f5efe3]/60"}`}>
              <Avatar person={{ name: n.fromUserName, initials: n.fromInitials, color: n.fromColor }} size={32} />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-500">
                  <span className="font-semibold text-slate-800">{n.fromUserName}</span> · {n.scope === "task" ? "Task" : "Project"}{" "}
                  <span className="font-medium text-slate-700">{n.refName}</span>
                </p>
                <p className="mt-0.5 line-clamp-2 text-sm text-slate-700">{n.preview}</p>
                <p className="mt-1 text-[11px] text-slate-400">{timeAgo(n.ts)}</p>
              </div>
              {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#b8975a]" />}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const [mobile, setMobile] = useState(false);
  const { openDialog } = useApp();
  const loc = useLocation();
  const title = loc.pathname.startsWith("/projects/") ? "Project Detail" : TITLES[loc.pathname] || "";

  return (
    <div className="flex h-screen overflow-hidden bg-[#f7f6f2]">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      {mobile && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobile(false)} />
          <div className="relative h-full w-64 animate-slide-in">
            <Sidebar onNavigate={() => setMobile(false)} />
            <button className="absolute right-3 top-5 text-white" onClick={() => setMobile(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 md:gap-4 md:px-6">
          <button className="md:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu-btn">
            <Menu className="h-6 w-6 text-slate-600" />
          </button>
          <h1 className="truncate text-lg font-bold text-slate-900">{title}</h1>
          <GlobalSearch />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-auto flex h-9 items-center gap-1.5 rounded-lg bg-[#00573f] px-3 text-sm font-semibold text-white transition-colors hover:bg-[#004330] sm:ml-0" data-testid="new-menu-btn">
                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="text-xs text-slate-400">Create new</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {[
                ["project", "Project", FolderKanban],
                ["task", "Task", ListChecks],
                ["customer", "Customer", Building2],
                ["team", "Team Member", Users],
                ["document", "Document", FileText],
              ].map(([k, l, I]) => (
                <DropdownMenuItem key={k} onClick={() => openDialog(k)} className="cursor-pointer gap-2" data-testid={`new-${k}`}>
                  <I className="h-4 w-4 text-slate-500" /> Add a New {l}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Notifications />
          <div className="hidden items-center gap-2 rounded-lg border border-[#e7dcc6] bg-[#fbf8f1] py-1 pl-2 pr-3 sm:flex">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#a08450]">Client</span>
            <HarrodsMark />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-6" key={loc.pathname}>
          <Outlet />
        </main>
      </div>
      <TaskSheet />
      <Dialogs />
    </div>
  );
}
